// The caller's role signal for the current tenant — the ONE thing the client
// knows about who is asking (`GET /v1/auth/tenants`). Shared by every area's
// client-side permission mirror (office/catalogue, onboarding, …) so there is
// exactly one fetch and one resolution rule.
//
// The server is the only authorisation boundary. These helpers exist so the UI
// does not OVER-SHOW controls a role cannot use; a hidden control is a
// convenience, never a security guarantee, and every derivation here is
// fail-closed.
//
// WHAT THE SIGNAL CARRIES (D-586). Each membership row names three lists:
//   * `role_codes`: the literal codes of the held roles;
//   * `role_lineage_codes`: the seed role each of them is a clone of (D-224);
//   * `permissions`: the permission codes those roles actually carry.
// The client reads them the way the server does:
//   * a permission check reads `permissions`, so a tenant's clone of a seed
//     role is offered what it holds, and a role the tenant trimmed is offered
//     less (`derivePermissions`, `holdsPermission`);
//   * a role-shaped default (which screens a salesperson works in, where a
//     floor worker lands, which narrowing a screen expects) reads the lineage,
//     as every service-side narrowing does since D-224 (`hasLineage`);
//   * the few checks the server makes on the LITERAL role (D-224 rulings 3
//     and 7: naming an owner, group_admin's own privileges) read the literal
//     codes, through `hasLiteralRole`, and each one carries a
//     `LITERAL-ROLE-CODES:` marker naming the server site it mirrors.
// `tools/ci/role-lineage-gate.ts` holds `apps/web/src` to that last rule.
//
// Before D-586 the feed carried role codes alone and every area mapped them
// through a hand-kept copy of the seed roles' grants, so a clone resolved to
// nothing and its user saw no office areas at all. Those copies
// (`*_ROLE_GRANTS`) remain, CI-checked against the contract, for one case: a
// feed row without `permissions` (a fixture, or a response from an API build
// older than D-586 during a deploy), where they reproduce the old answer.

import { type QueryClient, useQuery } from "@tanstack/react-query";
import { client } from "./api";
import { UnauthorizedError } from "./problem";
import { RequestError, unwrap } from "./request";

export interface Membership {
  readonly subdomain: string;
  readonly role_codes: readonly string[];
  /** `tenants.default_timezone` — the zone the SERVER evaluates business dates
   * in (§9.14). Optional on this type ONLY so a fixture written before the
   * field existed still compiles; the wire always carries it. */
  readonly default_timezone?: string;
  /** `tenants.name` — the tenant's display name (`TenantSummary.name`), read by
   * the office exports' provenance row. Optional on this type for the same
   * fixture reason as `default_timezone`; the wire always carries it. */
  readonly name?: string;
  /** The clone-lineage root of each held role (D-224, D-586). Optional on
   * this type ONLY for fixtures and for a pre-D-586 API during a deploy; an
   * absent list reads as `role_codes`, which is exact for every seed role. */
  readonly role_lineage_codes?: readonly string[];
  /** The permission codes the held roles carry (D-586). Optional for the same
   * reason; an absent list falls back to the system-role mirrors. */
  readonly permissions?: readonly string[];
}

/**
 * What the client knows about the caller's roles at this tenant (D-586).
 *
 * `roleLineageCodes` and `permissions` are optional so that every
 * hand-written fixture and test double that carries `roleCodes` alone still
 * describes a seed-role principal exactly: an absent lineage reads as the
 * literal codes, and absent (or null) permissions fall back to the
 * system-role mirrors.
 */
export interface RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  readonly roleLineageCodes?: ReadonlySet<string>;
  readonly permissions?: ReadonlySet<string> | null;
}

/** A role signal, or a bare set of literal role codes (a seed-role principal
 * described by its codes alone: the mirrors' own unit tests). */
export type RoleInput = ReadonlySet<string> | RoleSignal;

function signalOf(input: RoleInput): RoleSignal {
  return "roleCodes" in input ? input : { roleCodes: input };
}

/** The lineage roots the signal describes; the literal codes when the feed
 * carried none (exact for every seed role, which is its own root). */
export function lineageCodesOf(input: RoleInput): ReadonlySet<string> {
  const signal = signalOf(input);
  return signal.roleLineageCodes ?? signal.roleCodes;
}

/**
 * True when any held role descends from one of `roles` (D-224 ruling 2). The
 * default for every role-shaped check the client makes, because it is the key
 * the server's own narrowings use: a tenant's renamed clone of `sales` is a
 * salesperson to both.
 */
export function hasLineage(input: RoleInput, roles: Iterable<string>): boolean {
  const lineage = lineageCodesOf(input);
  for (const role of roles) if (lineage.has(role)) return true;
  return false;
}

/**
 * True when the caller holds one of `roles` by its LITERAL code. Only for a
 * check the server makes on the literal code (D-224 rulings 3 and 7: an
 * exemption or a privilege a clone must not inherit). Every call site carries
 * a `LITERAL-ROLE-CODES:` marker naming the server rule it mirrors;
 * `tools/ci/role-lineage-gate.ts` refuses a call without one.
 */
export function hasLiteralRole(input: RoleInput, roles: Iterable<string>): boolean {
  const literal = signalOf(input).roleCodes;
  for (const role of roles) if (literal.has(role)) return true;
  return false;
}

/**
 * True when the caller holds permission `code`. Reads the server's list when
 * the feed carried one; otherwise falls back to `fallbackRoles`, the seed
 * roles the contract grants the code to, matched literally (the pre-D-586
 * answer). For a code that no area mirror carries, such as
 * `identity.view_members`.
 */
export function holdsPermission(
  input: RoleInput,
  code: string,
  fallbackRoles: Iterable<string>,
): boolean {
  const signal = signalOf(input);
  if (signal.permissions !== undefined && signal.permissions !== null) {
    return signal.permissions.has(code);
  }
  for (const role of fallbackRoles) if (signal.roleCodes.has(role)) return true;
  return false;
}

/**
 * Can the client say what this caller holds? Always, once the feed carries
 * the permission list (D-586). Without it, only when every literal role is a
 * seed role the area's mirror knows; a role it does not know may hold any
 * code, so a screen that would refuse on the mirror's word lets the request
 * go and renders the server's answer instead.
 */
export function rolesAreKnown(
  input: RoleInput,
  grants: Readonly<Record<string, readonly string[]>>,
): boolean {
  const signal = signalOf(input);
  if (signal.permissions !== undefined && signal.permissions !== null) return true;
  for (const role of signal.roleCodes) if (!Object.hasOwn(grants, role)) return false;
  return true;
}

/** The three fields a permissions hook spreads into what it returns, so a
 * screen can hand the whole hook result to any `RoleInput` helper. */
export function roleSignalOf(feed: RoleSignal): RoleSignal {
  return {
    roleCodes: feed.roleCodes,
    roleLineageCodes: lineageCodesOf(feed),
    permissions: feed.permissions ?? null,
  };
}

/** Shared cache key so every area's permission hook reuses one fetch. */
export const AUTH_TENANTS_QUERY_KEY = ["auth", "tenants", "caps"] as const;

/**
 * Called on sign-in and on sign-out, beside `resetRefNames` (routes/login.tsx,
 * routes/root.tsx, routes/reset-password.tsx). The feed is cached for the life
 * of the page (`staleTime: Infinity`), and the QueryClient outlives an in-app
 * sign-out, so without this the next person on a shared browser was shown the
 * previous person's role home and area controls until a reload.
 */
export function resetRoleFeed(queryClient: QueryClient | undefined): void {
  queryClient?.removeQueries({ queryKey: AUTH_TENANTS_QUERY_KEY });
}

export interface ResolveRoleCodesOptions {
  /**
   * Dev-host escape hatch. On `localhost` (or any host that is not
   * `<sub>.assessly.ai`) a multi-tenant user matches no membership, which would
   * leave every control hidden. When true, the FIRST membership is used — one
   * real tenant's real roles, never a cross-tenant union. Off by default, and
   * only ever passed from `import.meta.env.DEV`.
   */
  readonly devFallbackToFirst?: boolean;
}

/**
 * Resolve the caller's role codes for the current tenant. One membership → use
 * it; otherwise the membership whose subdomain matches the current host.
 *
 * FAIL-CLOSED when nothing matches: the previous behaviour unioned every
 * membership's roles, which handed a user who is group_admin at tenant A the
 * install-pack / go-live / blocking-waive controls while looking at tenant B.
 * The server would refuse them, but offering an irreversible control that will
 * 403 is not a UI we want, and "fail closed" is the rule this whole mirror is
 * built on. An empty set means screens render read-only. Pure, so unit-tested
 * against explicit hosts.
 */
export function resolveRoleCodes(
  items: readonly Membership[],
  host: string,
  options: ResolveRoleCodesOptions = {},
): Set<string> {
  if (items.length === 0) return new Set();
  if (items.length === 1) {
    const only = items[0];
    return new Set(only ? only.role_codes : []);
  }
  const sub = host.split(".")[0] ?? "";
  const match = items.find((i) => i.subdomain === sub);
  if (match) return new Set(match.role_codes);
  if (options.devFallbackToFirst === true) {
    const first = items[0];
    return new Set(first ? first.role_codes : []);
  }
  return new Set();
}

/**
 * The caller's whole role signal for the current tenant (D-586), by the same
 * selection rule as `resolveRoleCodes`. Fail-closed the same way: no matching
 * membership is an empty signal whose permissions are an empty list, never a
 * fallback to the mirrors.
 */
export function resolveRoleSignal(
  items: readonly Membership[],
  host: string,
  options: ResolveRoleCodesOptions = {},
): RoleSignal {
  const membership = resolveCurrentMembership(items, host, options);
  if (membership === undefined) {
    return { roleCodes: new Set(), roleLineageCodes: new Set(), permissions: new Set() };
  }
  const roleCodes = new Set(membership.role_codes);
  return {
    roleCodes,
    roleLineageCodes:
      membership.role_lineage_codes === undefined
        ? roleCodes
        : new Set(membership.role_lineage_codes),
    permissions: membership.permissions === undefined ? null : new Set(membership.permissions),
  };
}

/**
 * The current tenant's business timezone, by the SAME selection rule as
 * `resolveRoleCodes` — one membership, else the host's subdomain, else the dev
 * escape hatch. `undefined` when nothing matches, which is exactly the case in
 * which `resolveRoleCodes` returns an empty set and the screen renders
 * read-only anyway.
 *
 * WHY IT IS SELECTED HERE and not read from some other route: the zone a client
 * needs is the one the SERVER prices and dates with — `tenants.default_timezone`
 * — and it arrives on the membership the role codes came from, so the two can
 * never describe different tenants. A facility's `timezone` is a different
 * value and would disagree with the save.
 *
 * Callers pass it to `facilityTodayIso`, whose documented fallback (browser
 * calendar when no zone is in hand) is what makes the loading window safe: the
 * date self-corrects when the feed lands, because the preview's question
 * carries the date and a changed question is re-asked.
 */
export function resolveTenantTimezone(
  items: readonly Membership[],
  host: string,
  options: ResolveRoleCodesOptions = {},
): string | undefined {
  if (items.length === 0) return undefined;
  if (items.length === 1) return items[0]?.default_timezone;
  const sub = host.split(".")[0] ?? "";
  const match = items.find((i) => i.subdomain === sub);
  if (match) return match.default_timezone;
  return options.devFallbackToFirst === true ? items[0]?.default_timezone : undefined;
}

/**
 * The current tenant's WHOLE membership row, by the same selection rule as
 * `resolveRoleCodes` and `resolveTenantTimezone` — one membership, else the
 * host's subdomain, else the dev escape hatch, else `undefined`. The name and
 * the subdomain an export stamps on its file come from here, so a file can
 * never name a different tenant than the permissions that produced it.
 */
export function resolveCurrentMembership(
  items: readonly Membership[],
  host: string,
  options: ResolveRoleCodesOptions = {},
): Membership | undefined {
  if (items.length === 0) return undefined;
  if (items.length === 1) return items[0];
  const sub = host.split(".")[0] ?? "";
  const match = items.find((i) => i.subdomain === sub);
  if (match) return match;
  return options.devFallbackToFirst === true ? items[0] : undefined;
}

/** The one place the dev escape hatch is decided, so no screen re-invents it. */
export function roleCodeResolutionOptions(): ResolveRoleCodesOptions {
  return { devFallbackToFirst: import.meta.env.DEV };
}

/**
 * An area's effective permission codes (pure, fail-closed).
 *
 * When the feed carried the caller's permissions (D-586), this is that list
 * cut to the codes the area's table names, so a tenant's clone of a seed role
 * is offered exactly what it holds and a trimmed role is offered less. The
 * cut keeps each area's vocabulary to its own namespace, which is all its
 * screens ever ask about.
 *
 * Otherwise (a fixture, or an API older than D-586) it is the old mirror:
 * each literal role code mapped through `grants`, the area's CI-checked slice
 * of the contract's `systemRoleGrants`. A role absent from `grants` adds
 * nothing there, so that path still fails closed.
 */
export function derivePermissions(
  input: RoleInput,
  grants: Readonly<Record<string, readonly string[]>>,
): Set<string> {
  const signal = signalOf(input);
  const out = new Set<string>();
  if (signal.permissions !== undefined && signal.permissions !== null) {
    const vocabulary = new Set<string>();
    for (const codes of Object.values(grants)) for (const code of codes) vocabulary.add(code);
    for (const code of signal.permissions) if (vocabulary.has(code)) out.add(code);
    return out;
  }
  for (const role of signal.roleCodes) {
    const granted = grants[role];
    if (granted !== undefined) for (const code of granted) out.add(code);
  }
  return out;
}

/** What every area's permission hook returns. `has` is fail-closed. */
export interface AreaPermissions {
  /** False until the role feed settles — `has` is fail-closed until then. */
  isResolved: boolean;
  /** True when the caller holds `code` (the feed's permission list, D-586). */
  has: (code: string) => boolean;
}

// ---------------------------------------------------------------------------
// THE ONE ROLE-FEED READ (surface review F13, 2026-08-12)
//
// Six area mirrors — catalogue, catalogue caps, inventory, production, waste,
// onboarding — each declared their OWN `useQuery` against
// `AUTH_TENANTS_QUERY_KEY`, all six copies identical and all six carrying the
// same two defects. Sharing a cache key made them one fetch; it did not make
// them one POLICY, so a fix had to be made six times or not at all. This is
// that one policy.
//
// DEFECT 1 — an HTTP failure was indistinguishable from "you belong to no
// tenants". Every copy read `const { data } = await client.GET(...)` and
// returned `data?.items ?? []`. openapi-fetch does not throw on a non-2xx: it
// returns `{ data: undefined, error }`. So a 500 or a 503 from the identity
// service resolved the query SUCCESSFULLY with an empty membership list. The
// caller then derived zero role codes, `isResolved` went true and
// `isFeedError` — the very flag `trace-viewer`, `unit-tabs`, `floor-count`,
// `floor-scan` and waste settings consult so they never turn "we cannot tell"
// into "you are not permitted" — stayed FALSE. The office sidebar dropped Set
// up, Production and Waste; the screens claimed a refusal the server never
// made. A failed read must READ AS FAILED.
//
// DEFECT 2 — `retry: false` made one blip permanent. With `staleTime` and
// `gcTime` at Infinity the errored result is never revisited, so a single
// dropped packet during the first paint of the session deleted those nav
// entries until the tab was reloaded. A transient network failure is not an
// authorisation answer and must not be recorded as one.
//
// The rule now: a REFUSAL (401/403) is final and is never retried — the server
// has answered and the answer is no. Anything else — offline, DNS, a 5xx, a
// gateway timeout — is retried with react-query's backoff, and `isResolved`
// stays false throughout, so the shell holds its reserving skeleton (§5
// render-once) instead of painting a truncated nav it would have to correct.
// Only when the retries are spent does the feed report an error, and THEN the
// absence is a real fact rather than a dropped packet.
// ---------------------------------------------------------------------------

/** Attempts after the first before the feed gives up. */
export const ROLE_FEED_MAX_RETRIES = 3;

/**
 * Retry policy for the role feed (pure, unit-tested). A 401 or a 403 is the
 * server's ANSWER — final, never retried. Everything else is transient until
 * proven otherwise.
 */
export function shouldRetryRoleFeed(failureCount: number, error: unknown): boolean {
  if (error instanceof UnauthorizedError) return false;
  if (error instanceof RequestError && (error.httpStatus === 401 || error.httpStatus === 403)) {
    return false;
  }
  return failureCount < ROLE_FEED_MAX_RETRIES;
}

/** The caller's memberships. Throws (rather than answering "none") on failure. */
async function fetchMemberships(signal: AbortSignal): Promise<readonly Membership[]> {
  const result = await client.GET("/v1/auth/tenants", { signal });
  // The failure branch, and only the failure branch, goes through the shared
  // `unwrap` — it raises `UnauthorizedError` for a 401 and a `RequestError`
  // carrying the problem's `request_id` / `code` for everything else, which is
  // what `shouldRetryRoleFeed` reads. A 2xx keeps the tolerant `?? []`: an
  // empty membership list is a legitimate answer for a user who has been
  // removed from every tenant, and it is only a LIE when the call failed.
  if (!result.response.ok || result.error !== undefined) {
    unwrap(result, "auth-tenants");
  }
  return result.data?.items ?? [];
}

// ---------------------------------------------------------------------------
// A REFUSAL REFRESHES THE FEED (D-586)
//
// The feed is read once per page load (`staleTime: Infinity`). Since D-586 the
// nav and the controls follow the permission list it carries, so when an
// admin trims a role the user kept being offered the trimmed area until they
// reloaded, while the server caught up within its 10-second grant cache
// (spec 16 §3.9 item 5). A `403 PERMISSION_DENIED` from any other read or
// write is the server saying the feed's picture is out of date, so it marks
// the feed stale and the next render offers what the role now holds.
//
// Bounded twice. The feed's own errors are ignored, so a feed that keeps
// failing cannot invalidate itself in a loop, and a refusal refreshes the feed
// at most once per server cache window, so a screen that a role is refused on
// every read costs one feed read per window rather than one per refusal.
// ---------------------------------------------------------------------------

/** The server's grant cache window (spec 16 §3.9 item 5, `GRANTS_CACHE_TTL_MS`):
 * a feed younger than this can describe nothing newer than it already does. */
export const ROLE_FEED_REFRESH_AFTER_MS = 10_000;

/**
 * Whether this error should mark the role feed stale (pure, unit-tested): a
 * `403 PERMISSION_DENIED`, when the feed holds data at least one server cache
 * window old. Any other error, a feed with no data yet (in flight, or failed),
 * or a feed fetched inside the window answers false.
 */
export function refusalStalesRoleFeed(
  error: unknown,
  feedUpdatedAt: number,
  nowMs: number,
): boolean {
  if (!(error instanceof RequestError)) return false;
  if (error.httpStatus !== 403 || error.code !== "PERMISSION_DENIED") return false;
  if (feedUpdatedAt <= 0) return false;
  return nowMs - feedUpdatedAt >= ROLE_FEED_REFRESH_AFTER_MS;
}

function isRoleFeedKey(key: readonly unknown[]): boolean {
  return (
    key.length === AUTH_TENANTS_QUERY_KEY.length &&
    AUTH_TENANTS_QUERY_KEY.every((part, i) => key[i] === part)
  );
}

/**
 * Watch every query and mutation on `queryClient` and mark the role feed
 * stale on a refusal (`refusalStalesRoleFeed`). Returns the unsubscribe.
 * Armed once, beside the client, in `main.tsx`.
 */
export function armRoleFeedRefresh(
  queryClient: QueryClient,
  now: () => number = () => Date.now(),
): () => void {
  const consider = (error: unknown): void => {
    const state = queryClient.getQueryState(AUTH_TENANTS_QUERY_KEY);
    if (state === undefined || state.fetchStatus === "fetching") return;
    if (!refusalStalesRoleFeed(error, state.dataUpdatedAt, now())) return;
    void queryClient.invalidateQueries({ queryKey: AUTH_TENANTS_QUERY_KEY, exact: true });
  };
  const stopQueries = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "error") return;
    if (isRoleFeedKey(event.query.queryKey as readonly unknown[])) return;
    consider(event.action.error);
  });
  const stopMutations = queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "error") return;
    consider(event.action.error);
  });
  return () => {
    stopQueries();
    stopMutations();
  };
}

/** The shared role feed, resolved for the current host. */
export interface RoleFeed extends RoleSignal {
  /** The caller's literal role codes for this tenant; empty until the feed
   * settles. */
  readonly roleCodes: ReadonlySet<string>;
  /** Their lineage roots (D-224, D-586); empty until the feed settles. */
  readonly roleLineageCodes: ReadonlySet<string>;
  /** The permission codes they carry (D-586): empty until the feed settles,
   * null when the API answered without the list. */
  readonly permissions: ReadonlySet<string> | null;
  /** False while the feed is in flight OR being retried — `has` is fail-closed. */
  readonly isResolved: boolean;
  /**
   * True when the feed FAILED, as opposed to "settled with no grants". A read
   * surface must not turn "we cannot tell" into "you are not permitted".
   */
  readonly isFeedError: boolean;
}

/**
 * The caller's role signal — ONE query, ONE retry policy, ONE resolution rule,
 * for every area mirror. Every hook that used to declare its own copy of this
 * query calls it instead.
 */
export function useRoleCodesFeed(): RoleFeed {
  const query = useQuery({
    queryKey: AUTH_TENANTS_QUERY_KEY,
    retry: shouldRetryRoleFeed,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async ({ signal }) => fetchMemberships(signal),
  });
  const signal: RoleSignal = query.data
    ? resolveRoleSignal(query.data, window.location.hostname, roleCodeResolutionOptions())
    : { roleCodes: new Set<string>(), roleLineageCodes: new Set<string>(), permissions: new Set() };
  return {
    roleCodes: signal.roleCodes,
    roleLineageCodes: lineageCodesOf(signal),
    permissions: signal.permissions ?? null,
    // `isFetched` is true the moment a retry-exhausted failure lands, so a
    // definitive error still resolves the shell — never an eternal skeleton.
    isResolved: query.isFetched,
    isFeedError: query.isError,
  };
}

/**
 * The current tenant's business timezone, off the SAME cached feed the role
 * codes come from — so it costs no request and can never name a different
 * tenant than the permissions do.
 *
 * `undefined` while the feed is in flight, and for a host that matches no
 * membership. Callers hand it straight to `facilityTodayIso`, which falls back
 * to the browser calendar in exactly those cases — the established contract
 * (D-089): a screen that is right nineteen hours a day beats a blank one, and
 * a question that carries the date is re-asked when the date changes.
 */
export function useTenantTimezone(): string | undefined {
  const query = useQuery({
    queryKey: AUTH_TENANTS_QUERY_KEY,
    retry: shouldRetryRoleFeed,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async ({ signal }) => fetchMemberships(signal),
  });
  return query.data
    ? resolveTenantTimezone(query.data, window.location.hostname, roleCodeResolutionOptions())
    : undefined;
}

/** The current tenant's membership row off the SAME cached feed — no request
 * of its own. `undefined` while the feed is in flight or when no membership
 * matches the host. */
function useCurrentMembership(): Membership | undefined {
  const query = useQuery({
    queryKey: AUTH_TENANTS_QUERY_KEY,
    retry: shouldRetryRoleFeed,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async ({ signal }) => fetchMemberships(signal),
  });
  return query.data
    ? resolveCurrentMembership(query.data, window.location.hostname, roleCodeResolutionOptions())
    : undefined;
}

/**
 * The current tenant's display name (`TenantSummary.name`), for the provenance
 * row an office export writes as line 2 of its file. `undefined` while the
 * feed is in flight; the export then leaves the name out rather than guess.
 */
export function useTenantName(): string | undefined {
  return useCurrentMembership()?.name;
}

/** The current tenant's subdomain, for an export's file name
 * (`sales-orders_<subdomain>_<date>.csv`). Read from the membership rather than
 * the host, because a dev host (`localhost`) is not a subdomain. */
export function useTenantSubdomain(): string | undefined {
  return useCurrentMembership()?.subdomain;
}
